import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import { AxiosError } from 'axios'
import CrossDialogBtn from 'common/Button/CrossDialogBtn'
import NormalText from 'common/Typography/NormalText'
import { Formik } from 'formik'
import { FormikHelpers } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as yup from 'yup'

// import { RefundDataCheck } from '../sharedComponents'
import { RefundDataCheckV2 } from './RefundDataCheckV2'

const validationSchema = yup.object({
  userId: yup.string().required('User ID is required'),
  ticketId: yup
    .string()
    .required('Ticket ID is required')
    .matches(/^[0-9]+$/, 'Must be only digits'),
  refundReason: yup.string().required('Refund reason is required'),
  refundAmount: yup
    .number()
    .min(0, 'Amount must be at least 0')
    .max(yup.ref('$maxAmount'), 'Amount exceeds the maximum allowed')
    .nullable()
})

export function RefundFormV3Dialog({
  allocationId,
  catalogName,
  transactionType,
  dialogOpen,
  handleDialogClose,
  paymentId,
  paymentIntent,
  amount,
  currency,
  iccid,
  userId,
  email,
  refetchPaymentLogs
}: RefundFormProps) {
  const faberToken = OrbitSession.get('faberToken')
  const [isDataUsageZero, setIsDataUsageZero] = useState(false)
  const [isSubmitRefundLoading, setIsSubmitRefundLoading] = useState(false)

  const handleRefundLoading = () => {
    setIsSubmitRefundLoading(true)
  }

  const { mutate: mutateRefundOtpack } = useMutation(
    PaymentAPI.partialRefundUser,
    {
      onSuccess: () => {
        toast.success('Refund for customer received')
        handleDialogClose()
        refetchPaymentLogs()
      },
      onError: (error: AxiosError) => {
        toast.error(error.message)
      },
      onSettled: () => {
        setIsSubmitRefundLoading(false)
      }
    }
  )

  const initialFormValues = useMemo(() => {
    return {
      userId: userId,
      invoiceID: paymentId,
      ticketId: '',
      refundReason: '',
      faberToken: faberToken,
      terminatePlan: false,
      currency: currency ?? ''
    }
  }, [isDataUsageZero, userId, paymentId])

  const handleFormSubmit = (
    values,
    actions: FormikHelpers<FormDetailsProps>
  ) => {
    const payload = {
      ...values,
      refundAmount: String(values.amount)
    }
    mutateRefundOtpack(payload, {
      onSettled: () => {
        actions.resetForm()
      }
    })
  }

  return (
    <Dialog
      open={dialogOpen}
      maxWidth='sm'
      fullWidth={true}
      onClose={handleDialogClose}
      aria-labelledby='draggable-dialog-title'
    >
      <Box sx={{ py: 2, px: 0 }}>
        <DialogTitle
          sx={{
            margin: '0',
            fontWeight: '700',
            color: 'black',
            fontSize: '21px',
            mt: '4px',
            px: 3
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1em', mx: 2 }}>
              Refund Form
            </Typography>
            <CrossDialogBtn dialogClose={handleDialogClose} />
          </Box>
          <NormalText sx={{ px: 2, my: 0, color: 'primary.dark', mx: 0 }}>
            Payment ID: {paymentIntent}
          </NormalText>
          <Typography
            sx={{
              color: '#9E9E9E',
              fontFamily: 'SF Pro Display, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              letterSpacing: '0.24px',
              px: 2,
              mt: 0
            }}
          >
            Required fields are marked with an asterisk *
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3 }}>
          <Formik
            validationSchema={validationSchema}
            initialValues={initialFormValues}
            onSubmit={(values, actions) => handleFormSubmit(values, actions)}
          >
            <RefundDataCheckV2
              allocationId={allocationId}
              catalogName={catalogName}
              transactionType={transactionType}
              currency={currency}
              amount={amount}
              mutateRefundOtpack={mutateRefundOtpack}
              key='data-check'
              faberToken={faberToken}
              iccid={iccid}
              refundDialogOpen={dialogOpen}
              setIsDataUsageZero={setIsDataUsageZero}
              customerEmail={email}
              isRefundLoading={isSubmitRefundLoading}
              handleRefundLoading={handleRefundLoading}
              customerId={userId}
              paymentId={paymentId}
              handleDialogClose={handleDialogClose}
            />
          </Formik>
        </DialogContent>
      </Box>
    </Dialog>
  )
}

interface RefundFormProps {
  allocationId?: string
  catalogName: string
  dialogOpen: boolean
  handleDialogClose: () => void
  paymentId: string
  paymentIntent: string
  refetchPaymentLogs: () => void
  iccid: string
  userId: string
  email: string
  handleRefundRequestTabChange?: () => void
  transactionType: string
  isExternalUser?: boolean
  amount: number
  currency?: string
}

export interface FormDetailsProps {
  userId: string
  ticketId: string
  refundReason: string
  invoiceID: string
  faberToken: string
  terminatePlan: boolean
  amount?: string
  currency: string
}
